import React, { createContext, useContext, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ar from "@/locales/ar.json";
import en from "@/locales/en.json";

const DICT = { ar, en };
export const SUPPORTED_LANGS = ["ar", "en"];
export const DEFAULT_LANG = "ar";

const I18nContext = createContext(null);

const getLangFromPath = (pathname) => {
  const seg = pathname.split("/").filter(Boolean)[0];
  return SUPPORTED_LANGS.includes(seg) ? seg : DEFAULT_LANG;
};

const resolveKey = (obj, key) => {
  return key.split(".").reduce((acc, part) => {
    if (acc && typeof acc === "object" && part in acc) return acc[part];
    return undefined;
  }, obj);
};

export const I18nProvider = ({ children, forcedLang }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = forcedLang || getLangFromPath(location.pathname);

  const t = useCallback(
    (key, fallback) => {
      const value = resolveKey(DICT[lang], key);
      if (value === undefined) return fallback !== undefined ? fallback : key;
      return value;
    },
    [lang],
  );

  const switchLang = useCallback(
    (newLang) => {
      if (!SUPPORTED_LANGS.includes(newLang)) return;
      const parts = location.pathname.split("/").filter(Boolean);
      if (SUPPORTED_LANGS.includes(parts[0])) {
        parts[0] = newLang;
      } else {
        parts.unshift(newLang);
      }
      const newPath = "/" + parts.join("/");
      // preserve scroll position: no scroll-to-top on language switch
      const scrollY = window.scrollY;
      navigate(newPath + location.search, { replace: false, state: { keepScroll: true } });
      requestAnimationFrame(() => window.scrollTo(0, scrollY));
    },
    [location.pathname, location.search, navigate],
  );

  const localePath = useCallback(
    (path) => {
      const clean = path.startsWith("/") ? path : "/" + path;
      if (clean === "/") return "/" + lang;
      return "/" + lang + clean;
    },
    [lang],
  );

  const value = useMemo(
    () => ({ t, lang, switchLang, localePath }),
    [t, lang, switchLang, localePath],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
};
